import type { TFunction } from 'i18next';
import type { Notification } from '../api/notifications.api.js';

/**
 * A NOTIFICATION IN THE READER'S LANGUAGE.
 *
 * The server writes `title` and `message` as finished English sentences, so an
 * Arabic reader got English in the bell. A row that also carries `messageKey`
 * is rendered from the catalogue (`notifications.msg.<key>.title|message`),
 * filled with `messageParams`. The stored English is the fallback: for rows
 * created before keys existed, and for a key this build does not know yet.
 */
export function notificationText(
  t: TFunction,
  n: Pick<Notification, 'title' | 'message' | 'messageKey' | 'messageParams'>
): { title: string; message: string } {
  if (!n.messageKey) return { title: n.title, message: n.message };
  const { key, params } = legacyLateInHours(n.messageKey, n.messageParams ?? {});
  const base = `notifications.msg.${key}`;
  return {
    title: t(`${base}.title`, { ...params, defaultValue: n.title }),
    message: t(`${base}.message`, { ...params, defaultValue: n.message }),
  };
}

/**
 * Late notices written before 2026-10-23 carry the delay as bare minutes
 * ("late 557m"). Read them as hours too, so the bell never mixes the two.
 */
function legacyLateInHours(key: string, p: Record<string, unknown>): { key: string; params: Record<string, unknown> } {
  if (key === 'lateArrivalOne' && typeof p.count === 'number' && p.count >= 60) {
    return { key: 'lateArrivalOneHours', params: { ...p, hours: Math.floor(p.count / 60), minutes: p.count % 60 } };
  }
  if (!key.startsWith('lateArrivalMany')) return { key, params: p };
  const out = { ...p };
  for (let i = 1; i <= 5; i++) {
    const m = out[`m${i}`];
    if (out[`k${i}`] === 'late' && out[`h${i}`] === undefined && typeof m === 'number' && m >= 60) {
      Object.assign(out, { [`k${i}`]: 'lateH', [`h${i}`]: Math.floor(m / 60), [`m${i}`]: m % 60 });
    }
  }
  return { key, params: out };
}
